import { sortBy } from 'lodash-es'
import { TreeViewBaseItem } from '@mui/x-tree-view-pro'
import { shortenString } from '@utils'

type TemplateItem = TemplateFileItem | TemplateSectionItem

export type TemplateTreeViewItem = TreeViewBaseItem<{
  id: string
  label: string
  alreadyAdded: boolean
  /** Already added, but templates below it are not yet */
  partiallyAdded: boolean
}>

export interface StructurePreviewNode {
  id: string
  name: string
  kind: 'section' | 'file'
  /**
   * existing: already in the structure, new: created by the selected templates,
   * moved: an existing custom file the save moves under its new template file
   */
  status: 'existing' | 'new' | 'moved'
  /** Template guid of a new node, used to deselect it again */
  templateGuid?: string
  children: StructurePreviewNode[]
}

/** Template tree of one category as items for the selection tree view */
export const toTemplateTreeItems = (
  templates: TemplateItem[],
  alreadyAddedTemplates: string[],
): TemplateTreeViewItem[] => {
  const added = new Set(alreadyAddedTemplates)
  // Returns the item and whether it or anything below it is not added yet
  const convert = (item: TemplateItem): [TemplateTreeViewItem, boolean] => {
    const children = (item.children ?? []).map(convert)
    const alreadyAdded = added.has(item.guid)
    const hasMissingBelow = children.some(([, missing]) => missing)
    return [
      {
        id: item.guid,
        label: item.type === 'file' ? shortenString(item.name, 50) : item.name,
        alreadyAdded,
        partiallyAdded: alreadyAdded && hasMissingBelow,
        children: children.map(([child]) => child),
      },
      !alreadyAdded || hasMissingBelow,
    ]
  }
  return templates.map(item => convert(item)[0])
}

const flattenTemplates = (templates: TemplateItem[]): TemplateItem[] =>
  templates.flatMap(item => [item, ...flattenTemplates(item.children ?? [])])

export const getTemplateGuids = (templates: TemplateItem[]): string[] =>
  flattenTemplates(templates).map(item => item.guid)

/** The template and every template below it */
export const getTemplateSubtreeGuids = (templates: TemplateItem[], guid: string): string[] => {
  const item = flattenTemplates(templates).find(template => template.guid === guid)
  return item ? getTemplateGuids([item]) : []
}

/**
 * Payload of the selected templates, in template tree order. A selected
 * template is nested under its parent when the parent is selected too;
 * otherwise it is sent on the root level and the backend places it by its
 * parent_guid (see buildStructurePreview).
 */
export const collectSelectedTemplates = (
  templates: TemplateItem[],
  selectedGuids: Set<string>,
): TemplateItem[] => {
  const added = new Set<string>()

  const copy = (item: TemplateItem): TemplateItem => {
    added.add(item.guid)
    return {
      guid: item.guid,
      parent_guid: item.parent_guid,
      name: item.name,
      type: item.type,
      children: (item.children ?? [])
        .filter(child => selectedGuids.has(child.guid) && !added.has(child.guid))
        .map(copy),
    } as TemplateItem
  }

  const roots: TemplateItem[] = []
  const visit = (items: TemplateItem[], isParentSelected: boolean) => {
    items.forEach(item => {
      const isSelected = selectedGuids.has(item.guid)
      if (isSelected && !isParentSelected && !added.has(item.guid)) roots.push(copy(item))
      visit(item.children ?? [], isSelected)
    })
  }
  visit(templates, false)

  return roots
}

const isCustomFile = (file: FileItem) =>
  file.template_key === null || file.template_key === undefined

/** Template instance guid of a template file: `<template_key>[.<element_guid>]` */
const getTemplateInstanceGuid = (file: FileItem) =>
  file.element_guid ? `${file.template_key}.${file.element_guid}` : (file.template_key as string)

/**
 * The documentation structure as it looks after saving the given templates.
 * Mirrors the backend (FilesUseCase.create_from_templates and
 * move_custom_files_to_template_position):
 * - every created item is appended at the end of its parent
 * - root-level sections are always created on the documentation root
 * - a root-level file goes into the existing folder whose template key (or
 *   guid) is its parent_guid, else below the existing template file with that
 *   template guid, else onto the documentation root
 * - children stay nested under their newly created parent
 * - custom files on the root level that belong to the element of a template
 *   file are moved under that template file; like the backend, which reads all
 *   files ordered by hierarchy position, the last template file of an element wins
 */
export const buildStructurePreview = (
  files: FileItem[],
  sections: SectionItem[],
  rootGuid: string,
  newTemplates: TemplateItem[],
): StructurePreviewNode[] => {
  const root: StructurePreviewNode = {
    id: rootGuid,
    name: '',
    kind: 'section',
    status: 'existing',
    children: [],
  }
  const nodes = new Map<string, StructurePreviewNode>([[rootGuid, root]])
  // Hierarchy position of every node within its parent
  const positions = new Map<StructurePreviewNode, number>()
  // Highest hierarchy position per parent, read from its children on the first
  // append only, so appending many items to one parent stays linear
  const highestPositions = new Map<StructurePreviewNode, number>()

  // Appends like the backend: after the highest hierarchy position in the parent
  const append = (parent: StructurePreviewNode, node: StructurePreviewNode) => {
    const highest =
      highestPositions.get(parent) ??
      parent.children.reduce((max, child) => Math.max(max, positions.get(child) ?? 0), 0)
    positions.set(node, highest + 1)
    highestPositions.set(parent, highest + 1)
    parent.children.push(node)
  }

  // The backend's folder list includes the documentation root folder itself
  const existingItems = sortBy(
    [...files, ...sections].filter(item => item.guid !== rootGuid),
    'hierarchy_position',
  )
  existingItems.forEach(item => {
    const node: StructurePreviewNode = {
      id: item.guid,
      name: item.name,
      kind: item.type === 'file' ? 'file' : 'section',
      status: 'existing',
      children: [],
    }
    nodes.set(item.guid, node)
    positions.set(node, item.hierarchy_position)
  })
  existingItems.forEach(item => {
    const node = nodes.get(item.guid) as StructurePreviewNode
    nodes.get(item.parent_guid ?? rootGuid)?.children.push(node)
  })

  if (newTemplates.length === 0) return root.children

  // Like the backend's dict comprehensions: the last item with a key wins
  const folderByTemplateKey = new Map<string, string>()
  const templateFileByGuid = new Map<string, string>()
  existingItems.forEach(item => {
    if (!item.template_key) return
    if (item.type === 'file') templateFileByGuid.set(getTemplateInstanceGuid(item), item.guid)
    else folderByTemplateKey.set(item.template_key, item.guid)
  })
  const folderGuids = new Set(sections.map(section => section.guid))

  const resolveRootParent = (template: TemplateItem): string => {
    if (template.type === 'section' || !template.parent_guid) return rootGuid
    return (
      folderByTemplateKey.get(template.parent_guid) ??
      (folderGuids.has(template.parent_guid) ? template.parent_guid : undefined) ??
      templateFileByGuid.get(template.parent_guid) ??
      rootGuid
    )
  }

  // Template files with their element guid, in creation order
  const templateFiles: [string, StructurePreviewNode][] = []
  files.forEach(file => {
    if (!isCustomFile(file) && file.element_guid) {
      templateFiles.push([file.element_guid, nodes.get(file.guid) as StructurePreviewNode])
    }
  })

  // Created depth-first like the backend: the item first, then its children
  const addNewNode = (template: TemplateItem, parent: StructurePreviewNode) => {
    const node: StructurePreviewNode = {
      id: `new:${template.guid}`,
      name: template.name,
      kind: template.type,
      status: 'new',
      templateGuid: template.guid,
      children: [],
    }
    append(parent, node)
    const [, ...elementGuidParts] = template.guid.split('.')
    if (template.type === 'file' && elementGuidParts.length > 0) {
      templateFiles.push([elementGuidParts.join('.'), node])
    }
    template.children?.forEach(child => addNewNode(child, node))
  }

  newTemplates.forEach(template => {
    const parent = nodes.get(resolveRootParent(template))
    if (parent) addNewNode(template, parent)
  })

  // Template file per element for moving custom files under it
  const templateFileByElement = new Map(
    sortBy(templateFiles, ([, node]) => positions.get(node) ?? 0),
  )

  const movedNodes = new Set<StructurePreviewNode>()
  sortBy(files, 'hierarchy_position').forEach(file => {
    if (!isCustomFile(file) || !file.element_guid || file.parent_guid !== rootGuid) return
    const target = templateFileByElement.get(file.element_guid)
    const node = nodes.get(file.guid)
    if (!target || !node) return

    movedNodes.add(node)
    node.status = 'moved'
    append(target, node)
  })

  return root.children.filter(child => !movedNodes.has(child))
}

/** Ids of all nodes that contain a new or moved node, to expand them */
export const getChangedAncestorIds = (nodes: StructurePreviewNode[]): string[] => {
  const ids: string[] = []
  const hasChange = (node: StructurePreviewNode): boolean => {
    const childChanged = node.children.map(hasChange).some(Boolean)
    if (childChanged) ids.push(node.id)
    return node.status !== 'existing' || childChanged
  }
  nodes.forEach(hasChange)
  return ids
}

/** Number of new nodes anywhere below the given node */
export const countNewDescendants = (node: StructurePreviewNode): number =>
  node.children.reduce(
    (count, child) => count + (child.status === 'new' ? 1 : 0) + countNewDescendants(child),
    0,
  )
